import { jsx, jsxs } from "react/jsx-runtime";
import { ConciergeSidebarWidget } from "@brandclub/bc-widgets";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { useState, useEffect, useCallback, useRef } from "react";
import ReactDOM from "react-dom";
import { useBrandAppConfig } from "../../api/cache/appConfigQueryOptions.js";
import { getHeadersWithUniqueVisitorId } from "../../Auth/index.js";
/* empty css                       */
import { IconSidebar, IconCompose, IconHome } from "./ConciergeIcons.js";
const PAGE_SIZE = 30;
const fetchConversations = async (apiBaseUrl, limit = PAGE_SIZE, cursor) => {
  const headers = await getHeadersWithUniqueVisitorId();
  const params = new URLSearchParams({ limit: String(limit) });
  if (cursor) params.set("cursor", cursor);
  const response = await fetch(`${apiBaseUrl}/concierge/conversations?${params}`, {
    method: "GET",
    headers: { "Content-Type": "application/json", ...headers }
  });
  if (!response.ok) throw new Error("Failed to fetch conversations");
  return response.json();
};
const deleteConversation = async (apiBaseUrl, conversationId) => {
  const headers = await getHeadersWithUniqueVisitorId();
  const response = await fetch(`${apiBaseUrl}/concierge/conversations/${conversationId}`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json", ...headers }
  });
  if (!response.ok) throw new Error("Failed to delete conversation");
};
const weightMap = {
  normal: 400,
  medium: 500,
  semibold: 600,
  bold: 700
};
const SidebarButton = ({ icon, label, onClick, isExpanded, ariaLabel, state, resolveColor }) => {
  const [isHovered, setIsHovered] = useState(false);
  return /* @__PURE__ */ jsxs("div", { className: "tw:relative", children: [
    /* @__PURE__ */ jsxs(
      "button",
      {
        onClick: (e) => {
          e.stopPropagation();
          onClick == null ? void 0 : onClick();
        },
        "aria-label": ariaLabel,
        onMouseEnter: () => setIsHovered(true),
        onMouseLeave: () => setIsHovered(false),
        className: "tw:flex tw:items-center tw:gap-2 tw:h-10 tw:px-2.5 tw:rounded tw:transition-colors tw:cursor-pointer hover:tw:bg-black/5 tw:w-full",
        children: [
          /* @__PURE__ */ jsx("div", { className: "tw:flex tw:items-center tw:justify-center tw:shrink-0 tw:size-5", children: icon }),
          /* @__PURE__ */ jsx(
            "span",
            {
              className: "tw:text-sm tw:font-medium tw:whitespace-nowrap tw:overflow-hidden tw:transition-opacity tw:duration-150",
              style: {
                opacity: isExpanded ? 1 : 0,
                width: isExpanded ? "auto" : 0,
                color: resolveColor(state.labelColor),
                fontSize: state.labelFontSize,
                fontWeight: weightMap[state.labelFontWeight] ?? 500
              },
              children: label
            }
          )
        ]
      }
    ),
    !isExpanded && isHovered && /* @__PURE__ */ jsx(
      "div",
      {
        className: "tw:absolute tw:left-[45px] tw:top-1/2 tw:-translate-y-1/2 tw:px-2 tw:h-8 tw:flex tw:items-center tw:z-50 tw:whitespace-nowrap",
        style: {
          backgroundColor: resolveColor(state.tooltipBgColor),
          color: resolveColor(state.tooltipTextColor),
          fontSize: state.tooltipFontSize,
          fontWeight: 600,
          lineHeight: "18px",
          borderRadius: state.tooltipBorderRadius
        },
        children: label
      }
    )
  ] });
};
const LogoWithTooltip = ({ state, icons, resolveColor, isExpanded, brandLogoUrl }) => {
  const [logoHovered, setLogoHovered] = useState(false);
  const brandUrl = `https://${state.brandLinkText}`;
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: `tw:flex tw:items-center tw:shrink-0 ${isExpanded ? "tw:px-2.5 tw:pt-3 tw:pb-6" : "tw:mt-3"}`,
      onClick: (e) => e.stopPropagation(),
      children: [
        /* @__PURE__ */ jsxs("div", { className: "tw:relative", children: [
          /* @__PURE__ */ jsx(
            "a",
            {
              href: brandUrl,
              target: "_blank",
              rel: "noopener noreferrer",
              onMouseEnter: () => setLogoHovered(true),
              onMouseLeave: () => setLogoHovered(false),
              className: `tw:flex tw:items-center tw:justify-center tw:rounded tw:transition-colors hover:tw:bg-black/5 tw:cursor-pointer tw:no-underline ${isExpanded ? "tw:p-1 tw:shrink-0 tw:min-h-10" : "tw:h-10 tw:px-2.5 tw:w-full"}`,
              "aria-label": state.brandLinkText,
              onClick: (e) => e.stopPropagation(),
              children: icons.logoIconUrl || brandLogoUrl ? /* @__PURE__ */ jsx(
                "img",
                {
                  src: icons.logoIconUrl || brandLogoUrl,
                  alt: "Brand",
                  className: "tw:object-contain",
                  style: {
                    height: state.logoIconSize,
                    width: state.logoIconSize
                  }
                }
              ) : /* @__PURE__ */ jsx(IconHome, { size: state.logoIconSize, style: { color: resolveColor(state.sidebarIconColor) } })
            }
          ),
          !isExpanded && logoHovered && /* @__PURE__ */ jsx(
            "div",
            {
              className: "tw:absolute tw:left-[45px] tw:top-1/2 tw:-translate-y-1/2 tw:px-2 tw:h-8 tw:flex tw:items-center tw:z-50 tw:whitespace-nowrap",
              style: {
                backgroundColor: resolveColor(state.tooltipBgColor),
                color: resolveColor(state.tooltipTextColor),
                fontSize: state.tooltipFontSize,
                fontWeight: 600,
                lineHeight: "18px",
                borderRadius: state.tooltipBorderRadius
              },
              children: state.brandLinkText
            }
          )
        ] }),
        isExpanded && /* @__PURE__ */ jsxs(
          "a",
          {
            href: brandUrl,
            target: "_blank",
            rel: "noopener noreferrer",
            className: "tw:ml-1.5 tw:whitespace-nowrap tw:overflow-hidden tw:no-underline",
            style: {
              fontSize: state.brandLinkFontSize,
              fontWeight: weightMap[state.labelFontWeight] ?? 500,
              color: resolveColor(state.brandLinkColor),
              lineHeight: "24px"
            },
            onClick: (e) => e.stopPropagation(),
            children: [
              state.brandLinkPrefix,
              /* @__PURE__ */ jsx("span", { style: { textDecoration: "underline", textDecorationSkipInk: "none" }, children: state.brandLinkText })
            ]
          }
        )
      ]
    }
  );
};
const HistoryItem = ({ conversation, isActive, onSelect, onDelete, state, resolveColor }) => {
  const [hovered, setHovered] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const menuRef = useRef(null);
  const dotBtnRef = useRef(null);
  const [dropdownPos, setDropdownPos] = useState(null);
  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpen]);
  const openMenu = useCallback(() => {
    if (dotBtnRef.current) {
      const rect = dotBtnRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + 4, left: rect.right });
    }
    setMenuOpen((p) => !p);
  }, []);
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: "tw:relative",
      onMouseEnter: () => setHovered(true),
      onMouseLeave: () => {
        setHovered(false);
        if (!menuOpen) setMenuOpen(false);
      },
      children: [
        /* @__PURE__ */ jsx(
          "button",
          {
            onClick: () => !deleting && onSelect(conversation.conversationId),
            style: {
              width: "100%",
              textAlign: "left",
              padding: `${state.chatItemPaddingY}px ${state.chatItemPaddingX}px`,
              paddingRight: hovered || menuOpen ? 32 : state.chatItemPaddingX,
              backgroundColor: isActive ? resolveColor(state.chatItemActiveBgColor, "rgba(220,221,220,0.5)") : hovered || menuOpen ? resolveColor(state.chatItemHoverBgColor, "rgba(220,221,220,0.3)") : "transparent",
              border: "none",
              cursor: deleting ? "default" : "pointer",
              transition: "background-color 0.15s ease, opacity 0.2s ease",
              borderRadius: state.chatItemBorderRadius,
              display: "block",
              opacity: deleting ? 0.5 : 1
            },
            children: /* @__PURE__ */ jsxs(
              "div",
              {
                className: "tw:flex tw:items-center tw:gap-2",
                style: {
                  fontSize: state.chatItemFontSize,
                  fontWeight: weightMap[state.chatItemFontWeight] ?? 500,
                  color: resolveColor(state.chatItemColor),
                  lineHeight: "24px"
                },
                children: [
                  deleting && /* @__PURE__ */ jsx(
                    "svg",
                    {
                      width: "14",
                      height: "14",
                      viewBox: "0 0 24 24",
                      fill: "none",
                      className: "tw:shrink-0",
                      style: { animation: "spin 1s linear infinite" },
                      children: /* @__PURE__ */ jsx(
                        "circle",
                        {
                          cx: "12",
                          cy: "12",
                          r: "10",
                          stroke: resolveColor(state.chatItemColor),
                          strokeWidth: "3",
                          strokeLinecap: "round",
                          strokeDasharray: "32",
                          strokeDashoffset: "12"
                        }
                      )
                    }
                  ),
                  /* @__PURE__ */ jsx(
                    "span",
                    {
                      style: {
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: state.chatItemLineClamp,
                        WebkitBoxOrient: "vertical"
                      },
                      children: conversation.title || conversation.preview
                    }
                  ),
                  conversation.status === "in_progress" && /* @__PURE__ */ jsx(
                    "svg",
                    {
                      width: "14",
                      height: "14",
                      viewBox: "0 0 24 24",
                      fill: "none",
                      className: "tw:shrink-0",
                      style: { animation: "spin 1s linear infinite" },
                      children: /* @__PURE__ */ jsx(
                        "circle",
                        {
                          cx: "12",
                          cy: "12",
                          r: "10",
                          stroke: resolveColor(state.chatItemColor),
                          strokeWidth: "3",
                          strokeLinecap: "round",
                          strokeDasharray: "32",
                          strokeDashoffset: "12"
                        }
                      )
                    }
                  )
                ]
              }
            )
          }
        ),
        (hovered || menuOpen) && !deleting && /* @__PURE__ */ jsx("div", { className: "tw:absolute tw:right-1 tw:top-1/2 tw:-translate-y-1/2", children: /* @__PURE__ */ jsx(
          "button",
          {
            ref: dotBtnRef,
            onClick: (e) => {
              e.stopPropagation();
              openMenu();
            },
            className: "tw:flex tw:items-center tw:justify-center tw:size-6 tw:rounded tw:border-none tw:cursor-pointer tw:bg-transparent hover:tw:bg-black/10 tw:transition-colors",
            "aria-label": "More options",
            children: /* @__PURE__ */ jsxs("svg", { width: "16", height: "16", viewBox: "0 0 16 16", fill: "none", children: [
              /* @__PURE__ */ jsx("circle", { cx: "8", cy: "3", r: "1.5", fill: resolveColor(state.chatItemColor) }),
              /* @__PURE__ */ jsx("circle", { cx: "8", cy: "8", r: "1.5", fill: resolveColor(state.chatItemColor) }),
              /* @__PURE__ */ jsx("circle", { cx: "8", cy: "13", r: "1.5", fill: resolveColor(state.chatItemColor) })
            ] })
          }
        ) }),
        menuOpen && dropdownPos && ReactDOM.createPortal(
          /* @__PURE__ */ jsx(
            "div",
            {
              ref: menuRef,
              className: "tw:fixed tw:z-[9999] tw:rounded-lg tw:shadow-lg tw:border tw:border-black/10 tw:py-1 tw:min-w-[120px]",
              style: {
                top: dropdownPos.top,
                left: dropdownPos.left,
                transform: "translateX(-100%)",
                backgroundColor: resolveColor(state.expandedBgColor, "#ffffff")
              },
              children: /* @__PURE__ */ jsxs(
                "button",
                {
                  onClick: async (e) => {
                    e.stopPropagation();
                    setMenuOpen(false);
                    setDeleting(true);
                    try {
                      await onDelete(conversation.conversationId);
                    } finally {
                      setDeleting(false);
                    }
                  },
                  className: "tw:flex tw:items-center tw:gap-2 tw:w-full tw:px-3 tw:py-2 tw:border-none tw:cursor-pointer tw:bg-transparent hover:tw:bg-black/5 tw:transition-colors tw:text-left",
                  style: {
                    fontSize: state.chatItemFontSize,
                    color: "#dc2626"
                  },
                  children: [
                    /* @__PURE__ */ jsxs(
                      "svg",
                      {
                        width: "14",
                        height: "14",
                        viewBox: "0 0 24 24",
                        fill: "none",
                        stroke: "currentColor",
                        strokeWidth: "2",
                        strokeLinecap: "round",
                        strokeLinejoin: "round",
                        children: [
                          /* @__PURE__ */ jsx("path", { d: "M3 6h18" }),
                          /* @__PURE__ */ jsx("path", { d: "M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" }),
                          /* @__PURE__ */ jsx("path", { d: "M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" })
                        ]
                      }
                    ),
                    "Delete"
                  ]
                }
              )
            }
          ),
          document.body
        )
      ]
    }
  );
};
const ConciergeSidebar = ({
  onConversationSelect,
  onComposeClick,
  currentConversationId,
  userProfile,
  apiBaseUrl = "/api-rewards",
  onSignOutClick,
  onHomeClick,
  brandLogoUrl,
  isExpanded: isExpandedProp,
  onToggle,
  mobileOverlay = false
}) => {
  const { data: brandAppConfig } = useBrandAppConfig(apiBaseUrl);
  const brandLinkTextFromConfig = (() => {
    var _a;
    const link = (_a = brandAppConfig == null ? void 0 : brandAppConfig.branchIOConfig) == null ? void 0 : _a.webAppBranchLink;
    if (!link) return void 0;
    try {
      return new URL(link).hostname;
    } catch {
      return link.replace(/^https?:\/\//, "").replace(/\/+$/, "");
    }
  })();
  const [internalExpanded, setInternalExpanded] = useState(false);
  const isControlled = isExpandedProp !== void 0;
  const isExpanded = isControlled ? isExpandedProp : internalExpanded;
  const [conversations, setConversations] = useState([]);
  const [nextCursor, setNextCursor] = useState(void 0);
  const [loadingMore, setLoadingMore] = useState(false);
  const hasInProgressConversations = conversations.some((c) => c.status === "in_progress");
  const handleToggle = () => {
    if (isControlled) onToggle == null ? void 0 : onToggle();
    else setInternalExpanded((p) => !p);
  };
  const queryClient = useQueryClient();
  const { isLoading } = useQuery({
    queryKey: ["conversations", apiBaseUrl],
    queryFn: async () => {
      const result = await fetchConversations(apiBaseUrl, PAGE_SIZE);
      setConversations(result.conversations);
      setNextCursor(result.nextCursor);
      return result;
    },
    enabled: isExpanded,
    staleTime: 5 * 6e4,
    gcTime: 10 * 6e4
  });
  useEffect(() => {
    if (!isExpanded || !hasInProgressConversations) {
      return;
    }
    const intervalId = window.setInterval(() => {
      queryClient.invalidateQueries({ queryKey: ["conversations", apiBaseUrl] });
    }, 3e3);
    return () => window.clearInterval(intervalId);
  }, [isExpanded, hasInProgressConversations, queryClient, apiBaseUrl]);
  const loadMore = useCallback(async () => {
    setLoadingMore(true);
    try {
      const result = await fetchConversations(apiBaseUrl, PAGE_SIZE, nextCursor);
      setConversations((prev) => [...prev, ...result.conversations]);
      setNextCursor(result.nextCursor);
    } catch {
    } finally {
      setLoadingMore(false);
    }
  }, [apiBaseUrl, nextCursor]);
  const handleDeleteConversation = useCallback(
    async (conversationId) => {
      try {
        await deleteConversation(apiBaseUrl, conversationId);
        setConversations((prev) => prev.filter((c) => c.conversationId !== conversationId));
        queryClient.invalidateQueries({ queryKey: ["conversations", apiBaseUrl] });
        if (currentConversationId === conversationId) {
          onComposeClick == null ? void 0 : onComposeClick();
        }
      } catch {
      }
    },
    [apiBaseUrl, queryClient, currentConversationId, onComposeClick]
  );
  const getInitials = () => {
    const f = (userProfile == null ? void 0 : userProfile.firstName) || "";
    const l = (userProfile == null ? void 0 : userProfile.lastName) || "";
    if (f && l) return `${f[0]}${l[0]}`.toUpperCase();
    if (f) return f.slice(0, 2).toUpperCase();
    return "";
  };
  const initials = getInitials();
  const isSignedIn = (userProfile == null ? void 0 : userProfile.isSignedIn) ?? false;
  const [avatarHovered, setAvatarHovered] = useState(false);
  return /* @__PURE__ */ jsx(
    ConciergeSidebarWidget,
    {
      widgetId: "concierge-sidebar",
      displayName: "Concierge Sidebar",
      wrapperStyle: {
        width: "auto",
        flexShrink: 0,
        display: "flex",
        height: "100%"
      },
      children: (widgetState, icons, resolveColor) => {
        const state = brandLinkTextFromConfig ? { ...widgetState, brandLinkText: brandLinkTextFromConfig } : widgetState;
        return /* @__PURE__ */ jsxs(
          "aside",
          {
            role: "complementary",
            "aria-expanded": isExpanded,
            onClick: !isExpanded ? handleToggle : void 0,
            className: mobileOverlay ? "tw:flex tw:flex-col tw:shrink-0 tw:h-full" : "tw:hidden tw:md:flex tw:flex-col tw:shrink-0 tw:h-full",
            style: {
              backgroundColor: resolveColor(isExpanded ? state.expandedBgColor : state.collapsedBgColor),
              width: isExpanded ? state.expandedWidth : state.collapsedWidth,
              transition: "width 0.25s ease-in-out",
              cursor: isExpanded ? void 0 : "pointer",
              borderRight: `1px solid ${resolveColor(state.borderColor)}`
            },
            children: [
              /* @__PURE__ */ jsx(
                LogoWithTooltip,
                {
                  state,
                  icons,
                  resolveColor,
                  isExpanded,
                  brandLogoUrl
                }
              ),
              /* @__PURE__ */ jsxs("div", { className: "tw:flex tw:flex-col", children: [
                isExpanded && /* @__PURE__ */ jsx(
                  SidebarButton,
                  {
                    icon: /* @__PURE__ */ jsx(IconSidebar, { size: state.sidebarIconSize, style: { color: resolveColor(state.closeIconColor) } }),
                    label: state.closeSidebarLabel,
                    onClick: handleToggle,
                    isExpanded,
                    ariaLabel: state.closeSidebarLabel,
                    state,
                    resolveColor
                  }
                ),
                !isExpanded && /* @__PURE__ */ jsx(
                  SidebarButton,
                  {
                    icon: /* @__PURE__ */ jsx(IconSidebar, { size: state.sidebarIconSize, style: { color: resolveColor(state.sidebarIconColor) } }),
                    label: state.openSidebarLabel,
                    onClick: handleToggle,
                    isExpanded,
                    ariaLabel: state.openSidebarLabel,
                    state,
                    resolveColor
                  }
                ),
                /* @__PURE__ */ jsx(
                  SidebarButton,
                  {
                    icon: /* @__PURE__ */ jsx(IconCompose, { size: state.composeIconSize, style: { color: resolveColor(state.composeIconColor) } }),
                    label: state.newChatLabel,
                    onClick: onComposeClick,
                    isExpanded,
                    ariaLabel: state.newChatLabel,
                    state,
                    resolveColor
                  }
                )
              ] }),
              isExpanded ? /* @__PURE__ */ jsxs("div", { className: "sidebar-history-enter tw:flex tw:flex-col tw:flex-1 tw:min-h-0 tw:mt-6 tw:overflow-hidden", children: [
                /* @__PURE__ */ jsx(
                  "div",
                  {
                    className: "tw:px-2.5 tw:shrink-0",
                    style: {
                      fontSize: state.chatHistoryTitleFontSize,
                      fontWeight: weightMap[state.chatHistoryTitleFontWeight] ?? 500,
                      color: resolveColor(state.chatHistoryTitleColor)
                    },
                    children: state.chatHistoryTitle
                  }
                ),
                /* @__PURE__ */ jsxs("div", { className: "tw:flex-1 tw:overflow-y-auto tw:mt-2 tw:px-1", children: [
                  isLoading && /* @__PURE__ */ jsx("div", { className: "tw:flex tw:justify-center tw:py-4", children: /* @__PURE__ */ jsx(LoadingSpinner, { color: resolveColor(state.chatHistoryTitleColor) }) }),
                  !isLoading && conversations.length === 0 && /* @__PURE__ */ jsx(
                    "div",
                    {
                      className: "tw:text-center tw:py-4",
                      style: {
                        fontSize: state.chatItemFontSize,
                        color: resolveColor(state.chatItemColor, "#999")
                      },
                      children: "No conversations yet"
                    }
                  ),
                  !isLoading && conversations.length > 0 && /* @__PURE__ */ jsxs("div", { className: "sidebar-history-list", children: [
                    conversations.map((c) => /* @__PURE__ */ jsx(
                      HistoryItem,
                      {
                        conversation: c,
                        isActive: c.conversationId === currentConversationId,
                        onSelect: (id) => onConversationSelect == null ? void 0 : onConversationSelect(id),
                        onDelete: handleDeleteConversation,
                        state,
                        resolveColor
                      },
                      c.conversationId
                    )),
                    !!nextCursor && /* @__PURE__ */ jsx(
                      "button",
                      {
                        onClick: (e) => {
                          e.stopPropagation();
                          loadMore();
                        },
                        disabled: loadingMore,
                        className: "tw:w-full tw:py-2 tw:px-2.5 tw:border-none tw:bg-transparent tw:cursor-pointer hover:tw:bg-black/5 tw:transition-colors tw:rounded disabled:tw:opacity-50",
                        style: {
                          fontSize: state.chatItemFontSize,
                          fontWeight: weightMap[state.chatItemFontWeight] ?? 500,
                          color: resolveColor(state.chatItemColor),
                          opacity: 0.7
                        },
                        children: loadingMore ? "Loading..." : "Load more..."
                      }
                    )
                  ] })
                ] })
              ] }) : /* @__PURE__ */ jsx("div", { className: "tw:flex-1" }),
              isSignedIn && /* @__PURE__ */ jsxs("div", { className: "tw:relative", onClick: (e) => e.stopPropagation(), children: [
                /* @__PURE__ */ jsxs(
                  "button",
                  {
                    "aria-label": state.signOutLabel,
                    onClick: (e) => {
                      e.stopPropagation();
                      onSignOutClick == null ? void 0 : onSignOutClick();
                    },
                    onMouseEnter: () => setAvatarHovered(true),
                    onMouseLeave: () => setAvatarHovered(false),
                    className: "tw:flex tw:items-center tw:gap-2 tw:h-10 tw:px-2.5 tw:mb-3 tw:cursor-pointer tw:w-full",
                    children: [
                      /* @__PURE__ */ jsx(
                        "div",
                        {
                          className: "tw:flex tw:items-center tw:justify-center tw:rounded-full tw:shrink-0",
                          style: {
                            width: state.avatarSize,
                            height: state.avatarSize,
                            backgroundColor: resolveColor(state.avatarBgColor),
                            color: resolveColor(state.avatarTextColor),
                            fontSize: state.avatarFontSize,
                            fontWeight: weightMap[state.avatarFontWeight] ?? 600,
                            lineHeight: "12px"
                          },
                          children: initials
                        }
                      ),
                      /* @__PURE__ */ jsx(
                        "span",
                        {
                          className: "tw:whitespace-nowrap tw:overflow-hidden tw:transition-opacity tw:duration-150",
                          style: {
                            opacity: isExpanded ? 1 : 0,
                            width: isExpanded ? "auto" : 0,
                            color: resolveColor(state.labelColor),
                            fontSize: state.labelFontSize,
                            fontWeight: weightMap[state.labelFontWeight] ?? 500
                          },
                          children: state.signOutLabel
                        }
                      )
                    ]
                  }
                ),
                !isExpanded && avatarHovered && /* @__PURE__ */ jsx(
                  "div",
                  {
                    className: "tw:absolute tw:left-[45px] tw:top-1/2 tw:-translate-y-1/2 tw:px-2 tw:h-8 tw:flex tw:items-center tw:z-50 tw:whitespace-nowrap",
                    style: {
                      backgroundColor: resolveColor(state.tooltipBgColor),
                      color: resolveColor(state.tooltipTextColor),
                      fontSize: state.tooltipFontSize,
                      fontWeight: 600,
                      lineHeight: "18px",
                      borderRadius: state.tooltipBorderRadius
                    },
                    children: state.signOutLabel
                  }
                )
              ] })
            ]
          }
        );
      }
    }
  );
};
const LoadingSpinner = ({ color }) => /* @__PURE__ */ jsxs("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", style: { animation: "spin 1s linear infinite" }, children: [
  /* @__PURE__ */ jsx("style", { children: `@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }` }),
  /* @__PURE__ */ jsx(
    "circle",
    {
      cx: "12",
      cy: "12",
      r: "10",
      stroke: color,
      strokeWidth: "3",
      strokeLinecap: "round",
      strokeDasharray: "32",
      strokeDashoffset: "12"
    }
  )
] });
export {
  ConciergeSidebar
};
//# sourceMappingURL=ConciergeSidebar.js.map
